/**
 * @fileoverview Route guard reproducing the Angular `connectionGuard`.
 * @module app/guards/RequireConnection
 * @description
 * React port of `connectionGuard` (`angular/src/app/guards/connection-guard.ts`).
 * Blocks navigation during a major service outage. Reproduces the Angular logic
 * exactly:
 *
 *   - Allow if the current path is `/service-outage` (prevents redirect loop).
 *   - If `!networkStatus.isOnline()` -> redirect to `/service-outage`.
 *   - Else `await health.checkAllHealth()`; if `overall === UNHEALTHY` ->
 *     redirect to `/service-outage`. HEALTHY / DEGRADED / UNKNOWN are permitted.
 *
 * The health check is async, so it runs in an effect: while pending a loading
 * state is shown, then the decision (render `<Outlet/>` or redirect) is applied.
 *
 * @author Liraz Amir
 */

import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useNetworkStatusStore } from '@/stores/network-status.store';
import { useHealthStore } from '@/stores/health.store';
import { HealthLevel } from '@/models/health-status.model';
import { GuardLoading } from './GuardLoading';

/** Outcome of the connection check. */
type Decision = 'pending' | 'allow' | 'outage';

export function RequireConnection() {
  const location = useLocation();
  const isOnOutagePage = location.pathname === '/service-outage';

  const [decision, setDecision] = useState<Decision>(
    isOnOutagePage ? 'allow' : 'pending',
  );

  useEffect(() => {
    // Always allow the outage page itself (prevents redirect loop).
    if (isOnOutagePage) {
      setDecision('allow');
      return;
    }

    let cancelled = false;
    setDecision('pending');

    // Network offline -> straight to outage page (no health check needed).
    if (!useNetworkStatusStore.getState().isOnline()) {
      setDecision('outage');
      return;
    }

    void useHealthStore
      .getState()
      .checkAllHealth()
      .then((status) => {
        if (cancelled) return;
        setDecision(status.overall === HealthLevel.UNHEALTHY ? 'outage' : 'allow');
      })
      .catch(() => {
        // Match Angular's permissiveness: an unknown/failed check does not block.
        if (!cancelled) setDecision('allow');
      });

    return () => {
      cancelled = true;
    };
  }, [isOnOutagePage, location.pathname]);

  if (decision === 'pending') {
    return <GuardLoading />;
  }

  if (decision === 'outage') {
    return <Navigate to="/service-outage" replace />;
  }

  return <Outlet />;
}

export default RequireConnection;
